// pages/RecruitmentPage.jsx
import { useState, useEffect } from 'react';
import recruitmentController from '../../controllers/recruitmentController';
import Recruitment from '../../models/Recruitment';
import { 
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown,
  Briefcase, Users, UserCheck, TrendingUp, Calendar, MapPin, Clock, Copy
} from 'lucide-react';

const RecruitmentPage = () => {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterDepartment, setFilterDepartment] = useState('All');
  const [filterJobType, setFilterJobType] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    jobTitle: '',
    department: '',
    positions: 1,
    jobDescription: '',
    requirements: '',
    experienceRequired: '',
    salaryRange: '',
    location: '',
    jobType: 'Full-time',
    status: 'Open',
    closingDate: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [sortField, setSortField] = useState('postedDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const departments = ['IT', 'HR', 'Finance', 'Sales', 'Marketing', 'Operations', 'Customer Service'];
  const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'];

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    let result = [...jobs];
    
    if (searchQuery) {
      result = result.filter(job =>
        job.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.location?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (filterStatus !== 'All') {
      result = result.filter(job => job.status === filterStatus);
    }
    
    if (filterDepartment !== 'All') {
      result = result.filter(job => job.department === filterDepartment);
    }
    
    if (filterJobType !== 'All') {
      result = result.filter(job => job.jobType === filterJobType);
    }
    
    result.sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    setFilteredJobs(result);
    setCurrentPage(1);
  }, [jobs, searchQuery, filterStatus, filterDepartment, filterJobType, sortField, sortDirection]);

  const fetchJobs = async () => {
    setLoading(true);
    const result = await recruitmentController.getAll({ page: 1, limit: 100 });
    if (result.success) {
      setJobs(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
    setFormErrors([]);
  };

  const handleAdd = () => {
    setFormData({
      jobTitle: '',
      department: '',
      positions: 1,
      jobDescription: '',
      requirements: '',
      experienceRequired: '',
      salaryRange: '',
      location: '',
      jobType: 'Full-time',
      status: 'Open',
      closingDate: ''
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await recruitmentController.create(formData);
    
    if (result.success) {
      setShowAddModal(false);
      fetchJobs();
      alert('✅ Job posting created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleEdit = (job) => {
    setSelectedJob(job);
    setFormData({
      jobTitle: job.jobTitle,
      department: job.department,
      positions: job.positions,
      jobDescription: job.jobDescription,
      requirements: job.requirements,
      experienceRequired: job.experienceRequired,
      salaryRange: job.salaryRange,
      location: job.location,
      jobType: job.jobType,
      status: job.status,
      closingDate: job.closingDate
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await recruitmentController.update(selectedJob.id, formData);
    
    if (result.success) {
      setShowEditModal(false);
      fetchJobs();
      alert('✅ Job posting updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }
    
    setFormSubmitting(false);
  };

  const handleView = (job) => {
    setSelectedJob(job);
    setShowViewModal(true);
  };

  const handleDelete = async (job) => {
    const isConfirmed = window.confirm(`Delete job posting: ${job.jobTitle}?`);
    if (!isConfirmed) return;

    const result = await recruitmentController.delete(job.id);
    
    if (result.success) {
      fetchJobs();
      alert('✅ Job posting deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleClone = async (job) => {
    const result = await recruitmentController.cloneJob(job.id);
    
    if (result.success) {
      fetchJobs();
      alert('✅ Job posting cloned successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = async () => {
    const result = await recruitmentController.export({ status: filterStatus, department: filterDepartment }, 'csv');
    if (result.success) {
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jobs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Jobs exported!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);

  const stats = {
    total: jobs.length,
    open: jobs.filter(j => j.status === 'Open').length,
    totalApplicants: jobs.reduce((sum, j) => sum + j.applicants, 0),
    shortlisted: jobs.reduce((sum, j) => sum + j.shortlisted, 0),
    hired: jobs.reduce((sum, j) => sum + j.hired, 0)
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const styles = {
    container: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    title: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b', margin: 0 },
    button: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
    secondaryButton: { backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0' },
    statsBar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    statLabel: { fontSize: '14px', color: '#64748b', marginBottom: '8px' },
    statValue: { fontSize: '28px', fontWeight: 'bold', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
    searchBox: { flex: 1, minWidth: '250px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' },
    select: { padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: '#fff', cursor: 'pointer' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none' },
    td: { padding: '16px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
    actionButton: { padding: '6px', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.2s' },
    pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #e2e8f0' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
    modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' },
    modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1e293b', margin: 0 },
    modalBody: { padding: '24px' },
    formGroup: { marginBottom: '20px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' },
    textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '100px', fontFamily: 'inherit' },
    errorList: { backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' },
    errorItem: { color: '#dc2626', fontSize: '14px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Recruitment Management</h1>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={handleExport}>
            <Download size={18} /> Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} /> Post Job
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Jobs</div>
            <Briefcase size={24} color="#3b82f6" />
          </div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Open Positions</div>
            <TrendingUp size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.open}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Total Applicants</div>
            <Users size={24} color="#3b82f6" />
          </div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.totalApplicants}</div>
        </div>
        
        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Shortlisted</div>
            <UserCheck size={24} color="#f59e0b" />
          </div>
          <div style={{ ...styles.statValue, color: '#f59e0b' }}>{stats.shortlisted}</div>
        </div>

        <div style={styles.statCard}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={styles.statLabel}>Hired</div>
            <UserCheck size={24} color="#10b981" />
          </div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.hired}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Search jobs..." 
            style={styles.searchInput} 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
        
        <select style={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="All">All Status</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
          <option value="On Hold">On Hold</option>
          <option value="Filled">Filled</option>
        </select>
        
        <select style={styles.select} value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}>
          <option value="All">All Departments</option>
          {departments.map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>

        <select style={styles.select} value={filterJobType} onChange={(e) => setFilterJobType(e.target.value)}>
          <option value="All">All Job Types</option>
          {jobTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Jobs Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading jobs...
          </div>
        ) : currentJobs.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No jobs found. Click "Post Job" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('jobTitle')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Job Title
                      {sortField === 'jobTitle' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('department')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Department
                      {sortField === 'department' && (sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th style={styles.th}>Positions</th>
                  <th style={styles.th}>Applicants</th>
                  <th style={styles.th}>Posted Date</th>
                  <th style={styles.th}>Closing Date</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentJobs.map((job) => (
                  <tr key={job.id} style={{ cursor: 'pointer' }} onClick={() => handleView(job)}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600' }}>{job.jobTitle}</div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                        {job.location} • {job.jobType}
                      </div>
                    </td>
                    <td style={styles.td}>{job.department}</td>
                    <td style={styles.td}>
                      <span style={{ fontWeight: '600', color: '#3b82f6' }}>{job.positions}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '500' }}>{job.applicants}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {job.shortlisted} shortlisted
                      </div>
                    </td>
                    <td style={styles.td}>{formatDate(job.postedDate)}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={14} color="#f59e0b" />
                        {formatDate(job.closingDate)}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: `${job.getStatusColor()}20`, color: job.getStatusColor() }}>
                        {job.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleView(job); }} title="View">
                          <Eye size={18} color="#64748b" />
                        </button>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleEdit(job); }} title="Edit">
                          <Edit2 size={18} color="#3b82f6" />
                        </button>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleClone(job); }} title="Clone">
                          <Copy size={18} color="#10b981" />
                        </button>
                        <button style={styles.actionButton} onClick={(e) => { e.stopPropagation(); handleDelete(job); }} title="Delete">
                          <Trash2 size={18} color="#ef4444" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredJobs.length)} of {filteredJobs.length} jobs
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '14px', color: '#64748b' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Post New Job' : 'Edit Job Posting'}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Job Title *</label>
                    <input type="text" name="jobTitle" style={styles.input} value={formData.jobTitle} onChange={handleInputChange} required placeholder="e.g. Senior Software Engineer" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Department *</label>
                    <select name="department" style={styles.input} value={formData.department} onChange={handleInputChange} required>
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Number of Positions *</label>
                    <input type="number" name="positions" style={styles.input} value={formData.positions} onChange={handleInputChange} required min="1" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Job Type</label>
                    <select name="jobType" style={styles.input} value={formData.jobType} onChange={handleInputChange}>
                      {jobTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Location</label>
                    <input type="text" name="location" style={styles.input} value={formData.location} onChange={handleInputChange} placeholder="e.g. New York, Remote" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Experience Required</label>
                    <input type="text" name="experienceRequired" style={styles.input} value={formData.experienceRequired} onChange={handleInputChange} placeholder="e.g. 3-5 years" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Salary Range</label>
                    <input type="text" name="salaryRange" style={styles.input} value={formData.salaryRange} onChange={handleInputChange} placeholder="e.g. $80,000 - $120,000" />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Closing Date</label>
                    <input type="date" name="closingDate" style={styles.input} value={formData.closingDate} onChange={handleInputChange} min={new Date().toISOString().split('T')[0]} />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Filled">Filled</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Job Description *</label>
                  <textarea name="jobDescription" style={{ ...styles.textarea, minHeight: '120px' }} value={formData.jobDescription} onChange={handleInputChange} required placeholder="Provide a detailed job description..."></textarea>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Requirements *</label>
                  <textarea name="requirements" style={{ ...styles.textarea, minHeight: '120px' }} value={formData.requirements} onChange={handleInputChange} required placeholder="List the job requirements and qualifications..."></textarea>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button type="button" style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowAddModal(false); setShowEditModal(false); }} disabled={formSubmitting}>
                  Cancel
                </button>
                <button type="submit" style={styles.button} disabled={formSubmitting}>
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Post Job' : 'Update Job')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedJob && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{selectedJob.jobTitle}</h2>
              <button style={{ ...styles.actionButton, padding: '8px' }} onClick={() => setShowViewModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Department</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedJob.department}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{ ...styles.badge, backgroundColor: `${selectedJob.getStatusColor()}20`, color: selectedJob.getStatusColor() }}>
                    {selectedJob.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Positions</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#3b82f6' }}>{selectedJob.positions}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Job Type</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedJob.jobType}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Location</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <MapPin size={16} color="#64748b" />
                    {selectedJob.location || 'Not specified'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Experience</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>{selectedJob.experienceRequired || 'Not specified'}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Salary Range</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#10b981' }}>{selectedJob.salaryRange || 'Not specified'}</div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Closing Date</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={16} color="#f59e0b" />
                    {formatDate(selectedJob.closingDate)}
                  </div>
                </div>
              </div>

              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#475569' }}>Recruitment Progress</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', fontSize: '14px' }}>
                  <div>
                    <div style={{ color: '#64748b', marginBottom: '4px' }}>Applicants</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#3b82f6' }}>{selectedJob.applicants}</div>
                  </div>
                  <div>
                    <div style={{ color: '#64748b', marginBottom: '4px' }}>Shortlisted</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#f59e0b' }}>{selectedJob.shortlisted}</div>
                  </div>
                  <div>
                    <div style={{ color: '#64748b', marginBottom: '4px' }}>Hired</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>{selectedJob.hired}</div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>Job Description</div>
                <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {selectedJob.jobDescription}
                </div>
              </div>

              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#1e293b' }}>Requirements</div>
                <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {selectedJob.requirements}
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => { setShowViewModal(false); handleEdit(selectedJob); }}>
                <Edit2 size={16} /> Edit Job
              </button>
              <button style={styles.button} onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruitmentPage;